/**
 * Icon system — v.1-D §4.19.
 *
 * 55+ stroke icons ported from the mockup's `primitives.jsx`. Each entry is a
 * function component `(p: IconProps) => JSX.Element` returning a stroked SVG
 * with the conventional 24×24 viewbox. The default props paint with
 * `currentColor` so any consuming text colour propagates.
 *
 * Dev-mode exposure (per CLAUDE.md §9.1 #5 canonical exposures): at module
 * load `window.__iconKeysCount` is populated with `Object.keys(I).length` so
 * the V.4 smoke test can verify >= 55 icons without importing the map.
 * Production bundles tree-shake the block via `import.meta.env.DEV`.
 */
import type { CSSProperties, JSX, ReactNode } from 'react';

export interface IconProps {
  size?: number;
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  'aria-hidden'?: boolean;
}

function Icon({
  size = 16,
  stroke = 'currentColor',
  fill = 'none',
  strokeWidth = 1.6,
  style,
  'aria-hidden': ariaHidden = true,
  children,
}: IconProps & { children: ReactNode }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden={ariaHidden}
    >
      {children}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The icon map. Names match the canonical mockup verbatim (§4.19).
// ---------------------------------------------------------------------------

type IconRenderer = (p?: IconProps) => JSX.Element;

export const I = {
  home:     (p) => <Icon {...p}><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /></Icon>,
  tv:       (p) => <Icon {...p}><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M9 21h6" /></Icon>,
  library:  (p) => <Icon {...p}><path d="M4 4h4v16H4z" /><path d="M10 4h4v16h-4z" /><path d="M16 4l4 16" /></Icon>,
  calendar: (p) => <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></Icon>,
  guide:    (p) => <Icon {...p}><path d="M4 4h16v16H4z" /><path d="M4 9h16M9 4v16" /></Icon>,
  shield:   (p) => <Icon {...p}><path d="M12 3l8 3v6c0 5-4 9-8 9s-8-4-8-9V6z" /></Icon>,
  layers:   (p) => <Icon {...p}><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 14l9 5 9-5" /></Icon>,
  inbox:    (p) => <Icon {...p}><path d="M3 13l3-9h12l3 9" /><path d="M3 13v6h18v-6" /><path d="M8 13a4 4 0 008 0" /></Icon>,
  bar:      (p) => <Icon {...p}><path d="M4 19V9M10 19V5M16 19v-7M22 19H2" /></Icon>,
  gauge:    (p) => <Icon {...p}><path d="M4 18a8 8 0 1116 0" /><path d="M12 18l4-6" /></Icon>,
  dollar:   (p) => <Icon {...p}><path d="M12 3v18" /><path d="M17 7a4 4 0 00-4-3H11a3 3 0 000 6h2a3 3 0 010 6h-2a4 4 0 01-4-3" /></Icon>,
  uptime:   (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
  network:  (p) => <Icon {...p}><circle cx="6" cy="6" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /><path d="M8 6h8M8 18h8M6 8v8M18 8v8" /></Icon>,
  player:   (p) => <Icon {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M10 9l5 3-5 3z" /></Icon>,
  pulse:    (p) => <Icon {...p}><path d="M3 12h4l2-6 4 12 2-6h6" /></Icon>,
  bot:      (p) => <Icon {...p}><rect x="5" y="7" width="14" height="12" rx="2" /><path d="M12 4v3M9 12h.01M15 12h.01" /></Icon>,
  history:  (p) => <Icon {...p}><path d="M3 12a9 9 0 109-9" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></Icon>,
  bell:     (p) => <Icon {...p}><path d="M6 16V11a6 6 0 1112 0v5l2 2H4z" /><path d="M10 20a2 2 0 004 0" /></Icon>,
  settings: (p) => <Icon {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.4 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.4 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.9.4l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.4-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.4-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.4H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.4l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.4 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z" /></Icon>,
  help:     (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 015 0c0 2-2.5 2-2.5 4" /><path d="M12 17h.01" /></Icon>,
  search:   (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></Icon>,
  chevR:    (p) => <Icon {...p}><path d="M9 6l6 6-6 6" /></Icon>,
  chevD:    (p) => <Icon {...p}><path d="M6 9l6 6 6-6" /></Icon>,
  chevU:    (p) => <Icon {...p}><path d="M6 15l6-6 6 6" /></Icon>,
  chevL:    (p) => <Icon {...p}><path d="M15 6l-6 6 6 6" /></Icon>,
  cmd:      (p) => <Icon {...p}><path d="M9 9h6v6H9z" /><path d="M6 9a3 3 0 113-3v3M18 9a3 3 0 10-3-3v3M6 15a3 3 0 103 3v-3M18 15a3 3 0 11-3 3v-3" /></Icon>,
  arrowUp:  (p) => <Icon {...p}><path d="M12 19V5M5 12l7-7 7 7" /></Icon>,
  arrowDn:  (p) => <Icon {...p}><path d="M12 5v14M5 12l7 7 7-7" /></Icon>,
  arrowL:   (p) => <Icon {...p}><path d="M19 12H5M12 5l-7 7 7 7" /></Icon>,
  arrowR:   (p) => <Icon {...p}><path d="M5 12h14M12 5l7 7-7 7" /></Icon>,
  minus:    (p) => <Icon {...p}><path d="M5 12h14" /></Icon>,
  plus:     (p) => <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>,
  upload:   (p) => <Icon {...p}><path d="M12 17V3M5 10l7-7 7 7" /><path d="M3 21h18" /></Icon>,
  download: (p) => <Icon {...p}><path d="M12 3v14M5 14l7 7 7-7" /><path d="M3 21h18" /></Icon>,
  user:     (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></Icon>,
  users:    (p) => <Icon {...p}><circle cx="9" cy="8" r="3.5" /><path d="M3 20a6 6 0 0112 0" /><path d="M16 6a3 3 0 010 6M22 20a6 6 0 00-6-6" /></Icon>,
  zap:      (p) => <Icon {...p}><path d="M13 3L4 14h7l-1 7 9-11h-7z" /></Icon>,
  film:     (p) => <Icon {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 4v16M17 4v16M3 9h18M3 15h18" /></Icon>,
  alert:    (p) => <Icon {...p}><path d="M12 3l10 18H2z" /><path d="M12 10v5M12 18h.01" /></Icon>,
  check:    (p) => <Icon {...p}><path d="M5 12l4 4L19 6" /></Icon>,
  checkCircle: (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></Icon>,
  x:        (p) => <Icon {...p}><path d="M6 6l12 12M18 6L6 18" /></Icon>,
  eye:      (p) => <Icon {...p}><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Icon>,
  eyeOff:   (p) => <Icon {...p}><path d="M3 3l18 18" /><path d="M10.6 6.1A10 10 0 0122 12s-1.5 2.6-4.1 4.7M6.5 6.6A10 10 0 002 12s4 7 10 7a9.7 9.7 0 003.5-.7" /><circle cx="12" cy="12" r="2" /></Icon>,
  refresh:  (p) => <Icon {...p}><path d="M21 12a9 9 0 11-3-6.7" /><path d="M21 4v6h-6" /></Icon>,
  clock:    (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
  cloud:    (p) => <Icon {...p}><path d="M7 18a5 5 0 010-10 6 6 0 0111.7 1.3A4 4 0 0118 18z" /></Icon>,
  globe:    (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a13 13 0 010 18M12 3a13 13 0 000 18" /></Icon>,
  server:   (p) => <Icon {...p}><rect x="3" y="4" width="18" height="6" rx="1" /><rect x="3" y="14" width="18" height="6" rx="1" /><path d="M7 7h.01M7 17h.01" /></Icon>,
  dot:      (p) => <Icon {...p}><circle cx="12" cy="12" r="2" fill="currentColor" /></Icon>,
  more:     (p) => <Icon {...p}><circle cx="5" cy="12" r="1.5" fill="currentColor" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><circle cx="19" cy="12" r="1.5" fill="currentColor" /></Icon>,
  filter:   (p) => <Icon {...p}><path d="M3 5h18l-7 9v6l-4-2v-4z" /></Icon>,
  edit:     (p) => <Icon {...p}><path d="M4 20l4-1 11-11-3-3L5 16z" /><path d="M14 6l3 3" /></Icon>,
  trash:    (p) => <Icon {...p}><path d="M4 6h16" /><path d="M6 6l1 14h10l1-14" /><path d="M10 6V4h4v2" /></Icon>,
  copy:     (p) => <Icon {...p}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5h10" /></Icon>,
  link:     (p) => <Icon {...p}><path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1" /></Icon>,
  lock:     (p) => <Icon {...p}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></Icon>,
  key:      (p) => <Icon {...p}><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9 3 3-3 3 2 2-2 2-2-2-3 3" /></Icon>,
  mail:     (p) => <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 7 9-7" /></Icon>,
  info:     (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></Icon>,
  archive:  (p) => <Icon {...p}><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v12h14V8" /><path d="M10 12h4" /></Icon>,
  play:     (p) => <Icon {...p}><path d="M7 5l12 7-12 7z" /></Icon>,
  pause:    (p) => <Icon {...p}><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></Icon>,
  stop:     (p) => <Icon {...p}><rect x="6" y="6" width="12" height="12" rx="1" /></Icon>,
  star:     (p) => <Icon {...p}><path d="M12 3l3 6 6 1-4.5 4.4L18 21l-6-3.2L6 21l1.5-6.6L3 10l6-1z" /></Icon>,
  flag:     (p) => <Icon {...p}><path d="M5 21V4" /><path d="M5 4h12l-2 4 2 4H5" /></Icon>,
  folder:   (p) => <Icon {...p}><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></Icon>,
  file:     (p) => <Icon {...p}><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" /><path d="M14 3v6h6" /></Icon>,
} satisfies Record<string, IconRenderer>;

// Dev-mode exposure for smoke-suite (V.4) assertion. Gated by
// `import.meta.env.DEV` so the production bundle drops the block entirely.
// The `window` guard prevents a ReferenceError under vitest's node env.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__iconKeysCount = Object.keys(I).length;
}
